import { Component, Prop, h, Element, State } from '@stencil/core';

/**
 * Brand logo element that renders an image with an optional link wrapper.
 * Supports lazy loading via IntersectionObserver and provides an accessible aria-label.
 * Used in navbars, footers, company-logo grids, and comparison tables.
 *
 * @example
 *   <me-logo href="/" aria-label="Company Logo"><img src="https://via.placeholder.com/120x40" alt="Logo" /></me-logo>
 * @slot default - {required}, <img>, string
 */
@Component({
  tag: 'me-logo',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Logo {
  /** Reference to the host element (<me-logo>) */
  @Element() host: HTMLElement;

  /**
   * Optional href for the logo
   */
  @Prop() href?: string;

  /**
   * Aria-label for accessibility
   * @default logo
   */
  @Prop() ariaLabel?: string = 'logo';

  /**
   * Additional class modifiers for styling
   */
  @Prop() modifiers?: string;

  /**
   * Optional src for the logo image
   * @mediaType image
   */
  @Prop() src!: string;
  /**
   * Optional alt text for the logo image
   * @content
   */
  @Prop() alt?: string = 'logo';

  /**
   * Intrinsic width of the logo image in pixels (e.g. 124). Emitted as the `width` attribute.
   * Set it together with `height` — the pair gives the browser an aspect ratio to reserve the
   * box before the image arrives, which is what keeps the logo from shifting the layout (CLS).
   * The rendered size still comes from CSS.
   */
  @Prop() width?: number | string;

  /**
   * Intrinsic height of the logo image in pixels (e.g. 42). Emitted as the `height` attribute.
   * See `width`.
   */
  @Prop() height?: number | string;

  /**
   * Enable lazy loading (default: true). Accepts boolean or string ('true'/'false').
   * When true, image loads only when near viewport using IntersectionObserver.
   * When false, loads immediately.
   */
  @Prop() lazy: boolean | string = true;

  /**
   * Internal state to track if image should be loaded
   */
  @State() inView: boolean = false;

  /**
   * IntersectionObserver instance for lazy loading
   */
  private observer?: IntersectionObserver;

  /**
   * Normalize the lazy prop to a boolean value.
   */
  private get isLazy(): boolean {
    if (typeof this.lazy === 'string') {
      return this.lazy === 'true';
    }
    return !!this.lazy;
  }

  componentWillLoad() {
    if (!this.isLazy) {
      this.inView = true;
    }
  }

  componentDidLoad() {
    if (this.isLazy) {
      this.setupIntersectionObserver();
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Sets up intersection observer for lazy loading.
   * Loads the image only when it enters the viewport.
   */
  private setupIntersectionObserver() {
    if (!this.host) return;

    this.observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          this.inView = true;
          this.observer?.disconnect();
        }
      },
      { rootMargin: '200px' }, // Preload before the image is visible
    );

    this.observer.observe(this.host);

    // Deferred viewport check — avoids forced synchronous reflow during componentDidLoad.
    // IntersectionObserver handles most cases; this covers the edge case where the
    // observer callback doesn't fire for already-visible elements on first render.
    requestAnimationFrame(() => {
      if (!this.host || !this.observer) return;
      const rect = this.host.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0) {
        this.inView = true;
        this.observer?.disconnect();
      }
    });
  }

  render() {
    const Tag = this.href ? 'a' : 'span';
    const classes = `me-logo ${this.modifiers || ''}`.trim();

    // Prepare img attributes
    const imgAttributes: any = {
      alt: this.alt,
    };

    // Only set src when image should be loaded (lazy loading)
    if (this.inView) {
      imgAttributes.src = this.src;
    }

    // HTML img width/height attributes require integers (pixels). Props may arrive as numbers
    // (124) or CSS strings ("124px") — same normalization as me-picture.
    if (this.width !== undefined) {
      const w = typeof this.width === 'number' ? this.width : parseInt(String(this.width), 10);
      if (!isNaN(w)) imgAttributes.width = w;
    }
    if (this.height !== undefined) {
      const h = typeof this.height === 'number' ? this.height : parseInt(String(this.height), 10);
      if (!isNaN(h)) imgAttributes.height = h;
    }

    return (
      <Tag class={classes} {...(this.href && { href: this.href })} {...(this.ariaLabel && { 'aria-label': this.ariaLabel })}>
        {this.src ? <img alt={this.alt} {...imgAttributes} /> : <slot></slot>}
      </Tag>
    );
  }
}
